/* eslint-disable */
import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import clsx from 'clsx';
import { isBoolean, isInteger } from 'lodash';
import { DIMENSION } from '../../plugins/flexboxgrid/config';

/**
 * NOTE: The column offset should be handled by margin-left
 *
 * @Example `xsOffset={1} => mobile -> margin-left: 100% * 1 / 8; laptop -> margin-left: 100% * 1 / 10; desktop -> margin-left: 100% * 1 / 12 => className="ml-[12.5%] md:ml-[10%] lg:ml-[8.333333333333334%]"`
 * @Example `smOffset={2} => tablet -> margin-left: 100% * 2 / 8; laptop -> margin-left: 100% * 2 / 10; desktop -> margin-left: 100% * 2 / 12  => className="sm:ml-[25%] md:ml-[20%] lg:ml-[16.666666666666668%]"`
 */
const Col = React.forwardRef((props, ref) => {
  const { children, xs, sm, md, lg, className, reverse, ...colProps } = props;

  const reverseCssClass = useMemo(() => {
    return reverse && 'pff-flexbox-column--reverse';
  }, [reverse]);

  const isValidColValue = (colVal) => {
    return isInteger(colVal) || isBoolean(colVal);
  };

  const generateCol = (dimension, colVal) => {
    let colWidthValue = '';

    // Integer value
    if (isInteger(colVal)) {
      if (colVal === 0) {
        // Hide element
        if (dimension === DIMENSION.sm) {
          colWidthValue = 'sm:pff-flexbox-column--hidden';
        } else if (dimension === DIMENSION.md) {
          colWidthValue = 'md:pff-flexbox-column--hidden';
        } else if (dimension === DIMENSION.lg) {
          colWidthValue = 'lg:pff-flexbox-column--hidden';
        } else {
          colWidthValue = 'pff-flexbox-column--hidden';
        }
      } else {
        if (dimension === DIMENSION.sm) {
          if (colVal === 1) {
            colWidthValue = 'sm:pff-flexbox-column--1/8';
          } else if (colVal === 2) {
            colWidthValue = 'sm:pff-flexbox-column--2/8';
          } else if (colVal === 3) {
            colWidthValue = 'sm:pff-flexbox-column--3/8';
          } else if (colVal === 4) {
            colWidthValue = 'sm:pff-flexbox-column--4/8';
          } else if (colVal === 5) {
            colWidthValue = 'sm:pff-flexbox-column--5/8';
          } else if (colVal === 6) {
            colWidthValue = 'sm:pff-flexbox-column--6/8';
          } else if (colVal === 7) {
            colWidthValue = 'sm:pff-flexbox-column--7/8';
          } else if (colVal === 8) {
            colWidthValue = 'sm:pff-flexbox-column--8/8';
          }
        } else if (dimension === DIMENSION.md) {
          if (colVal === 1) {
            colWidthValue = 'md:pff-flexbox-column--1/10';
          } else if (colVal === 2) {
            colWidthValue = 'md:pff-flexbox-column--2/10';
          } else if (colVal === 3) {
            colWidthValue = 'md:pff-flexbox-column--3/10';
          } else if (colVal === 4) {
            colWidthValue = 'md:pff-flexbox-column--4/10';
          } else if (colVal === 5) {
            colWidthValue = 'md:pff-flexbox-column--5/10';
          } else if (colVal === 6) {
            colWidthValue = 'md:pff-flexbox-column--6/10';
          } else if (colVal === 7) {
            colWidthValue = 'md:pff-flexbox-column--7/10';
          } else if (colVal === 8) {
            colWidthValue = 'md:pff-flexbox-column--8/10';
          } else if (colVal === 9) {
            colWidthValue = 'md:pff-flexbox-column--9/10';
          } else if (colVal === 10) {
            colWidthValue = 'md:pff-flexbox-column--10/10';
          }
        } else if (dimension === DIMENSION.lg) {
          if (colVal === 1) {
            colWidthValue = 'lg:pff-flexbox-column--1/12';
          } else if (colVal === 2) {
            colWidthValue = 'lg:pff-flexbox-column--2/12';
          } else if (colVal === 3) {
            colWidthValue = 'lg:pff-flexbox-column--3/12';
          } else if (colVal === 4) {
            colWidthValue = 'lg:pff-flexbox-column--4/12';
          } else if (colVal === 5) {
            colWidthValue = 'lg:pff-flexbox-column--5/12';
          } else if (colVal === 6) {
            colWidthValue = 'lg:pff-flexbox-column--6/12';
          } else if (colVal === 7) {
            colWidthValue = 'lg:pff-flexbox-column--7/12';
          } else if (colVal === 8) {
            colWidthValue = 'lg:pff-flexbox-column--8/12';
          } else if (colVal === 9) {
            colWidthValue = 'lg:pff-flexbox-column--9/12';
          } else if (colVal === 10) {
            colWidthValue = 'lg:pff-flexbox-column--10/12';
          } else if (colVal === 11) {
            colWidthValue = 'lg:pff-flexbox-column--11/12';
          } else if (colVal === 12) {
            colWidthValue = 'lg:pff-flexbox-column--12/12';
          }
        } else {
          if (colVal === 1) {
            colWidthValue = 'pff-flexbox-column--1/8';
          } else if (colVal === 2) {
            colWidthValue = 'pff-flexbox-column--2/8';
          } else if (colVal === 3) {
            colWidthValue = 'pff-flexbox-column--3/8';
          } else if (colVal === 4) {
            colWidthValue = 'pff-flexbox-column--4/8';
          } else if (colVal === 5) {
            colWidthValue = 'pff-flexbox-column--5/8';
          } else if (colVal === 6) {
            colWidthValue = 'pff-flexbox-column--6/8';
          } else if (colVal === 7) {
            colWidthValue = 'pff-flexbox-column--7/8';
          } else if (colVal === 8) {
            colWidthValue = 'pff-flexbox-column--8/8';
          }
        }
      }
      // Boolean value
    } else if (colVal) {
      // Auto-width
      if (dimension === DIMENSION.sm) {
        colWidthValue = 'sm:pff-flexbox-column--show';
      } else if (dimension === DIMENSION.md) {
        colWidthValue = 'md:pff-flexbox-column--show';
      } else if (dimension === DIMENSION.lg) {
        colWidthValue = 'lg:pff-flexbox-column--show';
      } else {
        colWidthValue = 'pff-flexbox-column--show';
      }
    } else {
      // Hide element
      if (dimension === DIMENSION.sm) {
        colWidthValue = 'sm:pff-flexbox-column--hidden';
      } else if (dimension === DIMENSION.md) {
        colWidthValue = 'md:pff-flexbox-column--hidden';
      } else if (dimension === DIMENSION.lg) {
        colWidthValue = 'lg:pff-flexbox-column--hidden';
      } else {
        colWidthValue = 'pff-flexbox-column--hidden';
      }
    }

    return colWidthValue;
  };

  // Every class emitted below is a Tailwind responsive class (`sm:`/`md:`/`lg:` prefixed) whose
  // breakpoints are defined in tailwind.screens.js to match sizes.containerMinWidth exactly
  // (415/769/1281). The browser's own media queries therefore decide which one wins, and since
  // all four selectors have equal specificity and Tailwind emits them in ascending order, the
  // largest matching breakpoint applies.
  //
  // That makes emitting all four unconditionally both correct and free. It used to be gated on a
  // measured `viewWidth`, which meant the server — where there is no window to measure — could
  // only ever emit a subset, and the missing classes were filled in after hydration. Deriving
  // them from the props alone puts the right layout in the server-rendered HTML, so the first
  // paint is already correct and nothing reflows. It also drops a throttled resize listener per
  // column instance, of which a page renders many.
  //
  // Each breakpoint still falls back to the nearest smaller one that was supplied, matching how
  // these props have always cascaded.
  const cols = useMemo(() => {
    const cascade = (...candidates) => candidates.find(isValidColValue);
    const build = (dimension, value) => (value === undefined ? undefined : generateCol(dimension, value));

    return {
      xsCol: build('', cascade(xs)),
      smCol: build(DIMENSION.sm, cascade(sm, xs)),
      mdCol: build(DIMENSION.md, cascade(md, sm, xs)),
      lgCol: build(DIMENSION.lg, cascade(lg, md, sm, xs)),
    };
  }, [xs, sm, md, lg]);

  const { xsCol, smCol, mdCol, lgCol } = cols;

  const cssClass = useMemo(
    () => clsx('pff-flexbox-column', reverseCssClass, xsCol, smCol, mdCol, lgCol, className),
    [reverseCssClass, xsCol, smCol, mdCol, lgCol, className],
  );

  return (
    <div {...colProps} className={cssClass} ref={ref}>
      {children}
    </div>
  );
});

Col.propTypes = {
  children: PropTypes.node,
  className: PropTypes.string,
  reverse: PropTypes.bool,
  xs: PropTypes.oneOfType([PropTypes.number, PropTypes.bool]),
  sm: PropTypes.oneOfType([PropTypes.number, PropTypes.bool]),
  md: PropTypes.oneOfType([PropTypes.number, PropTypes.bool]),
  lg: PropTypes.oneOfType([PropTypes.number, PropTypes.bool]),
};

export default Col;
